import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ImagePlus, RefreshCw, X, Loader2, Sparkles } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
} from '../ui/responsive-modal';
import { api, AiLimitError } from '../../lib/api';
import { notify } from '../../lib/toast';
import { cn } from '../../lib/utils';

interface IllustrationModalProps {
  /** The scene text to illustrate */
  selection: string;
  projectId: string;
  chapterId?: string;
  /** Called when user clicks "Inserir no capítulo" — receives the data URL */
  onInsert: (imageUrl: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * IllustrationModal
 * Shows the AI-generated scene illustration with options to insert, download or regenerate.
 */
export function IllustrationModal({
  selection,
  projectId,
  chapterId,
  onInsert,
  open,
  onOpenChange,
}: IllustrationModalProps) {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Generate (or regenerate) the illustration
  const generate = useCallback(async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      const result = await api.illustrateScene({
        projectId,
        chapterId,
        selection,
      });
      setImageUrl(result.imageUrl);
      setPrompt(result.prompt);
      setHasGenerated(true);
    } catch (error: unknown) {
      if (error instanceof AiLimitError) {
        notify.aiLimitReached('cover');
        onOpenChange(false);
      } else {
        notify.error(
          t('illustrationModal.generateFailedTitle', 'Falha ao gerar ilustração'),
          t('illustrationModal.generateFailedDescription', 'Tente novamente em instantes.'),
        );
      }
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, projectId, chapterId, selection, onOpenChange]);

  // Auto-generate when modal opens for the first time
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (nextOpen && !hasGenerated && !isGenerating) {
        // Small delay so the modal renders first
        setTimeout(() => generate(), 80);
      }
      if (!nextOpen) {
        // Reset state on close so next open is fresh
        setImageUrl(null);
        setPrompt(null);
        setHasGenerated(false);
      }
    },
    [onOpenChange, hasGenerated, isGenerating, generate],
  );

  // Download the image
  const handleDownload = useCallback(() => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `ilustracao-cena-${Date.now()}.png`;
    link.click();
  }, [imageUrl]);

  // Insert into editor
  const handleInsert = useCallback(() => {
    if (!imageUrl) return;
    onInsert(imageUrl);
    onOpenChange(false);
    notify.success(t('illustrationModal.insertedSuccess', 'Ilustração inserida no capítulo'));
  }, [imageUrl, onInsert, onOpenChange]);

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      <ResponsiveModalContent className="sm:max-w-xl">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Sparkles size={15} className="text-primary" />
            </div>
            <ResponsiveModalTitle>{t('illustrationModal.title', 'Ilustrar cena')}</ResponsiveModalTitle>
          </div>
          <ResponsiveModalDescription>
            {t('illustrationModal.description', 'Ilustração gerada a partir do trecho selecionado.')}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        {/* Image area */}
        <div
          className={cn(
            'relative rounded-xl overflow-hidden bg-muted/40 border border-border',
            'flex items-center justify-center',
            isGenerating ? 'aspect-square' : imageUrl ? 'aspect-square' : 'aspect-square',
          )}
        >
          {isGenerating && (
            <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
              <Loader2 size={36} className="animate-spin text-primary" />
              <span className="text-sm">{t('illustrationModal.generating', 'Gerando ilustração...')}</span>
              <span className="text-xs text-muted-foreground/60">{t('illustrationModal.generatingHint', 'Isso pode levar até 20 segundos')}</span>
            </div>
          )}

          {!isGenerating && !imageUrl && (
            <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
              <ImagePlus size={36} className="opacity-40" />
              <span className="text-sm">{t('illustrationModal.noneGenerated', 'Nenhuma ilustração gerada')}</span>
            </div>
          )}

          {!isGenerating && imageUrl && (
            <img
              src={imageUrl}
              alt={t('illustrationModal.imageAlt', 'Ilustração da cena')}
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Prompt preview */}
        {prompt && !isGenerating && (
          <div className="px-3 py-2 rounded-lg bg-muted/50 border border-border">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground/70">{t('illustrationModal.promptLabel', 'Prompt: ')}</span>
              {prompt}
            </p>
          </div>
        )}

        <ResponsiveModalFooter className="flex-row gap-2 pt-0">
          {/* Regenerate */}
          <button
            onClick={generate}
            disabled={isGenerating}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              'border border-border text-muted-foreground hover:text-foreground hover:bg-muted',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isGenerating ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            {t('illustrationModal.regenerate', 'Regenerar')}
          </button>

          <div className="flex-1" />

          {/* Download */}
          <button
            onClick={handleDownload}
            disabled={!imageUrl || isGenerating}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              'border border-border text-muted-foreground hover:text-foreground hover:bg-muted',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <Download size={14} />
            {t('illustrationModal.download', 'Baixar')}
          </button>

          {/* Insert */}
          <button
            onClick={handleInsert}
            disabled={!imageUrl || isGenerating}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <ImagePlus size={14} />
            {t('illustrationModal.insert', 'Inserir no capítulo')}
          </button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
